import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useNitroStore } from "../../../../store/Store";
import { useIsComponentMounted } from "../../../../hooks/useIsComponentMounted";
import { useFavoriteOrigins } from "../../../../hooks/useFavoriteOrigins";
import AdditionalFiltersModal from "../additionalFilters";
import "./index.css";
import FilterObj from "./components/filters";
import MobileFilter from "./components/mobileFilter";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import HeaderProductFilter from "../../../../components/filters/HeaderProductFilter";
import NitroService from "../../../../utils/nitro.service";
import { IPinExtension } from "../../../../types/ILocation";
import { LabelType } from "../../../../store/CompetitiveAnalysisStore";

type PartyTypes = string[];

const Filters = ({
  searchDisabled,
  defaultProduct,
  disableProductFilter,
  onFooterSelect,
}: {
  searchDisabled: boolean;
  defaultProduct: string;
  disableProductFilter: boolean;
  onFooterSelect?: (tab: any) => void;
}) => {
  const isMobile = useIsMobile(768);
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();
  const user = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;

  const [
    setIsLoading,
    setFilters,
    currentFilters,
    product,
    setProduct,
    mode,
    setMode,
    partyTypes,
    setPartyTypes,
    showCompetitiveAnalysisModal,
    setSearchStarted,
    currentField,
    setCurrentField,
    labelType,
    setLabelType,
    showMobileFavoriteOriginPanel,
    fetchFavoriteOrigins,
    originManagerHasUnsavedPriceEdits,
    triggerOriginManagerUnsavedNavAttempt,
    originManagerUnsavedNavAttemptSource,
    originManagerUnsavedNavResolutionNonce,
    originManagerUnsavedNavResolutionAction,
    triggerOriginManagerCloseRequest,
    pendingWhereFilterUpdate,
    setPendingWhereFilterUpdate,
  ] = useNitroStore((states) => [
    states.setIsLoading,
    states.setFilters,
    states.currentFilters,
    states.product,
    states.setProduct,
    states.mode,
    states.setMode,
    states.partyTypes,
    states.setPartyTypes,
    states.showCompetitiveAnalysisModal,
    states.setSearchStarted,
    states.activeFilter,
    states.setActiveFilter,
    states.labelType,
    states.setLabelType,
    states.showMobileFavoriteOriginPanel,
    states.fetchFavoriteOrigins,
    states.originManagerHasUnsavedPriceEdits,
    states.triggerOriginManagerUnsavedNavAttempt,
    states.originManagerUnsavedNavAttemptSource,
    states.originManagerUnsavedNavResolutionNonce,
    states.originManagerUnsavedNavResolutionAction,
    states.triggerOriginManagerCloseRequest,
    states.pendingWhereFilterUpdate,
    states.setPendingWhereFilterUpdate,
  ]);

  const pendingBlockedSearchRef = useRef<{
    filters: {
      party: string;
      partyTypes: any[];
      city: string;
      state: string;
      location: string;
      month: string;
    };
    mode: string;
    product: string;
    partyTypes: any[];
    hasAdditionalFilterChange: boolean;
    labelTypeSnapshot: string;
    shouldCloseOriginManager: boolean;
  } | null>(null);

  const [featureFlags, favoriteOriginsByUser] = useNitroStore((state) => [
    state.featureFlags,
    state.favoriteOriginsByUser,
  ]);
  const { originManager } = featureFlags;

  const productRef = useRef<HTMLInputElement | null>(null);
  const locationDetailsRequestIdRef = useRef(0);
  const skipUnsavedPopupForNextSearchRef = useRef(false);

  const [tempParty, setTempParty] = useState(currentFilters.party);
  const [tempPartyTypes, setTempPartyTypes] = useState(partyTypes);

  // Initialize search type based on URL params first, then fallback to current filters
  // this is to fix the url construct issue on Quote and details page
  const qParty = searchParams.get("party");
  const qpCity = searchParams.get("city");
  const qpState = searchParams.get("state");
  const qpLocation = searchParams.get("location");

  let initialSearchType: "location" | "company" = "company";
  if (qParty) {
    initialSearchType = "company";
  } else if (
    qpCity ||
    qpState ||
    qpLocation ||
    currentFilters.city ||
    currentFilters.state ||
    currentFilters.location
  ) {
    initialSearchType = "location";
  }
  const [tempSearchType, setTempSearchType] = useState<"location" | "company">(
    initialSearchType,
  );

  const mobileInitialProduct = isMobile ? "AMMONIA" : defaultProduct;

  const [tempProduct, setTempProduct] = useState(
    product || mobileInitialProduct,
  );

  const [tempMode, setTempMode] = useState(mode);
  const [tempCity, setTempCity] = useState(currentFilters.city);
  const [tempState, setTempState] = useState(currentFilters.state);
  const [tempLocation, setTempLocation] = useState(currentFilters.location);
  const [tempMonth, setTempMonth] = useState(currentFilters.month);

  useEffect(() => {
    setTempParty(currentFilters.party);
    setTempPartyTypes(partyTypes);
    setTempMode(mode);
    setTempMonth(currentFilters.month);
  }, [currentFilters.party, currentFilters.month, mode, partyTypes]);

  useEffect(() => {
    setTempProduct(product);
  }, [product]);

  useEffect(() => {
    setTempCity(currentFilters.city);
    setTempState(currentFilters.state);
    setTempLocation(currentFilters.location);
  }, [currentFilters.city, currentFilters.state, currentFilters.location]);

  const isComponentMounted = useIsComponentMounted();
  // On load, get query params
  useEffect(() => {
    if (!isComponentMounted) return;
    const qpPartyType = searchParams.get("partyTypes");
    const qpProduct = searchParams.get("product");
    const qpMode = searchParams.get("mode");

    const { party, city, state, location } = currentFilters;

    const parsedPartyTypes = JSON.parse(
      decodeURI(qpPartyType || JSON.stringify(partyTypes)),
    );

    setPartyTypes(parsedPartyTypes);
    setProduct(qpProduct || product || defaultProduct);
    setFilters({
      party: qParty || party,
      mode: qpMode || mode,
      city: qpCity || city,
      state: qpState || state,
      location: qpLocation || location,
    });

    if (qParty !== null) setTempParty(qParty);
    if (qpPartyType !== null) {
      setTempPartyTypes(parsedPartyTypes);
    }
    if (qpProduct !== null) setTempProduct(qpProduct);
    if (qpMode !== null) setTempMode(qpMode);
    if (qpCity !== null) {
      setTempCity(qpCity);
    }
    if (qpState !== null) setTempState(qpState);
    if (qpLocation !== null) setTempLocation(qpLocation);

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComponentMounted]);

  // Set query params when filters change
  useEffect(() => {
    if (!isComponentMounted) return;
    const { party, city, state, location } = currentFilters;
    const params: { [key: string]: string } = {
      party,
      partyTypes: encodeURI(JSON.stringify(partyTypes)),
      product,
      mode,
      city,
      state,
      location,
    };
    Object.keys(params).forEach((key: string) => {
      if (!params[key] || params[key] === "") {
        delete params[key];
      }
    });

    setSearchParams(params);
  }, [
    isComponentMounted,
    setSearchParams,
    currentFilters,
    product,
    mode,
    partyTypes,
  ]);

  useEffect(() => {
    if (!isComponentMounted) return;
    if (!product) {
      productRef.current?.focus();
    }
  }, [isComponentMounted, product, productRef]);

  const search = (params?: {
    newProduct?: string;
    newMode?: string;
    newPartyTypes?: PartyTypes;
    newMonth?: string;
    newCity?: string;
    newState?: string;
    newLocation?: string;
    newParty?: string;
  }) => {
    setCurrentField(null);

    const skipUnsavedPopupForThisSearch =
      skipUnsavedPopupForNextSearchRef.current;
    skipUnsavedPopupForNextSearchRef.current = false;

    const newFilters = {
      party: params?.newParty || tempParty,
      partyTypes:
        params?.newPartyTypes !== undefined
          ? params?.newPartyTypes
          : tempPartyTypes,
      city: params?.newCity || tempCity,
      state: params?.newState || tempState,
      location: params?.newLocation || tempLocation,
      month: params?.newMonth || tempMonth,
    };
    const nextMode = params?.newMode ?? tempMode;
    const nextProduct = params?.newProduct ?? tempProduct;
    const nextPartyTypes = params?.newPartyTypes ?? tempPartyTypes;

    setMode(nextMode);
    setProduct(nextProduct);
    setPartyTypes(nextPartyTypes);

    const filterKeys = ["city", "state", "location", "party", "month"] as const;
    const hasWhoWhereWhenChange = filterKeys.some(
      (key) => newFilters[key] !== currentFilters[key],
    );

    if (hasWhoWhereWhenChange || hasAdditionalFilterChange) {
      if (
        originManagerHasUnsavedPriceEdits &&
        hasWhoWhereWhenChange &&
        !skipUnsavedPopupForThisSearch
      ) {
        pendingBlockedSearchRef.current = {
          filters: newFilters,
          mode: nextMode,
          product: nextProduct,
          partyTypes: nextPartyTypes,
          hasAdditionalFilterChange,
          labelTypeSnapshot: labelType || "",
          shouldCloseOriginManager: true,
        };
        triggerOriginManagerUnsavedNavAttempt("search");
        return;
      }

      const type = labelType;
      setSearchStarted(true);
      setFilters(newFilters);

      if (hasAdditionalFilterChange) {
        setLabelType(type);
      }
    }
  };

  // A newly added location's city/state becomes the WHERE filter, triggering a new search
  useEffect(() => {
    if (!pendingWhereFilterUpdate) return;

    const citySearchTerm = `${pendingWhereFilterUpdate.city}, ${pendingWhereFilterUpdate.state}`;
    setTempCity(citySearchTerm);
    setTempLocation("");
    search({
      newCity: citySearchTerm,
      newLocation: "",
    });
    setPendingWhereFilterUpdate(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingWhereFilterUpdate]);

  useEffect(() => {
    if (!originManagerUnsavedNavResolutionNonce) {
      return;
    }

    // Only apply pending blocked filter/product changes for search-triggered attempts.
    // Close-triggered resolutions should not consume stale search/product pending state.
    if (originManagerUnsavedNavAttemptSource !== "search") {
      pendingBlockedSearchRef.current = null;
      return;
    }

    const pending = pendingBlockedSearchRef.current;
    if (!pending || !originManagerUnsavedNavResolutionAction) {
      return;
    }

    pendingBlockedSearchRef.current = null;
    setCurrentField(null);
    setMode(pending.mode);
    setProduct(pending.product);
    setPartyTypes(pending.partyTypes);
    setSearchStarted(true);
    setFilters(pending.filters);

    if (pending.hasAdditionalFilterChange) {
      setLabelType(pending.labelTypeSnapshot as LabelType);
    }

    if (pending.shouldCloseOriginManager) {
      triggerOriginManagerCloseRequest();
    }
  }, [
    originManagerUnsavedNavAttemptSource,
    originManagerUnsavedNavResolutionNonce,
    originManagerUnsavedNavResolutionAction,
    setCurrentField,
    setMode,
    setProduct,
    setPartyTypes,
    setSearchStarted,
    setFilters,
    setLabelType,
    triggerOriginManagerCloseRequest,
  ]);

  const mobileSearch = (mobileValues?: {
    party?: string;
    city?: string;
    state?: string;
    location?: string;
    month?: string;
  }) => {
    search({
      newParty: mobileValues?.party ?? tempParty,
      newCity: mobileValues?.city ?? tempCity,
      newState: mobileValues?.state ?? tempState,
      newLocation: mobileValues?.location ?? tempLocation,
      newMonth: mobileValues?.month ?? tempMonth,
    });
  };

  useEffect(() => {
    // Hide filters when all filters are empty
    if (!tempCity && !tempState && !tempLocation && !tempParty) {
      setSearchStarted(false);
      search({ newMonth: tempMonth });
    } else {
      search({
        newCity: tempCity,
        newState: tempState,
        newLocation: tempLocation,
        newParty: tempParty,
        newMonth: tempMonth,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setSearchStarted,
    tempCity,
    tempState,
    tempLocation,
    tempParty,
    tempMonth,
  ]);

  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);

  // Auto focus on product field when competitive analysis modal is open and no product is selected
  useEffect(() => {
    setCurrentField(null);
  }, [setCurrentField, currentFilters, showCompetitiveAnalysisModal]);

  const [hasChange, setHasChange] = useState(false);
  useEffect(() => {
    const { party, city, state, location, month } = currentFilters;
    if (
      tempParty !== party ||
      tempCity !== city ||
      tempState !== state ||
      tempLocation !== location ||
      tempMonth !== month
    ) {
      setHasChange(true);
    } else {
      setHasChange(false);
    }
  }, [
    setHasChange,
    tempParty,
    tempCity,
    tempState,
    tempLocation,
    tempMonth,
    currentFilters,
  ]);

  const [hasAdditionalFilterChange, setHasAdditionalFilterChange] =
    useState(false);

  // Ensure we always have values for the hook, even on initial load
  const effectiveUser = user || "";
  const effectiveProduct = product || "AMMONIA";

  const isOriginManagerMobile = isMobile && originManager;

  const {
    kochOrigins,
    isLoading: legacyIsLoading,
    competitorOrigins,
  } = useFavoriteOrigins(
    isOriginManagerMobile ? "" : effectiveUser,
    isOriginManagerMobile ? "" : effectiveProduct,
  );

  // On mobile, add favorite origins to locations store so they appear on the map
  useEffect(() => {
    if (!isMobile) return;

    const isStillLoading = isOriginManagerMobile
      ? fetchFavoriteOrigins
      : legacyIsLoading;
    if (isStillLoading) return;

    const requestId = locationDetailsRequestIdRef.current + 1;
    locationDetailsRequestIdRef.current = requestId;

    let allOrigins: Array<{
      location: { id: string; name: string };
      competitor: boolean;
      truckEnabled: boolean;
      railEnabled: boolean;
      truckMaxPrice: number | null | undefined;
      truckMinPrice: number | null | undefined;
      railMaxPrice: number | null | undefined;
      railMinPrice: number | null | undefined;
    }>;

    if (isOriginManagerMobile) {
      const productFavorites =
        favoriteOriginsByUser.find(
          (o) =>
            o.product.toUpperCase() === (product || "AMMONIA").toUpperCase(),
        )?.favorites || [];
      allOrigins = productFavorites.map((f) => {
        const outboundModes = f.location.outboundModes || [];
        return {
          location: { id: f.location.id, name: f.location.name },
          competitor: f.competitor,
          truckEnabled: outboundModes.some(
            (m) => m === "TRUCK" || m === "TRUNK",
          ),
          railEnabled: outboundModes.includes("RAIL"),
          truckMaxPrice: null,
          truckMinPrice: null,
          railMaxPrice: null,
          railMinPrice: null,
        };
      });
    } else {
      allOrigins = [...kochOrigins, ...competitorOrigins].map((o) => ({
        location: { id: o.location.id, name: o.location.name },
        competitor: o.competitor,
        truckEnabled: Boolean(o.truckEnabled),
        railEnabled: Boolean(o.railEnabled),
        truckMaxPrice: o.truckMaxPrice,
        truckMinPrice: o.truckMinPrice,
        railMaxPrice: o.railMaxPrice,
        railMinPrice: o.railMinPrice,
      }));
    }

    if (allOrigins.length === 0) {
      // Clear locations when there are no favorite origins for the selected product
      if (requestId !== locationDetailsRequestIdRef.current) {
        return;
      }
      useNitroStore.setState({
        locations: [],
        partyTypes: [],
      });
      return;
    }

    const fetchLocationDetails = async () => {
      try {
        const locationIds = allOrigins
          .map((origin) => origin.location.id)
          .filter(Boolean);

        const locationDetailsResults =
          (await NitroService.getMultiLocationDetails({ ids: locationIds })) ||
          [];

        const locationDetailsById = new Map(
          locationDetailsResults.map((detail) => [detail?.id, detail]),
        );

        const toMonthKey = (month: unknown): string => {
          if (typeof month === "string") return (month as string).slice(0, 7);
          if (month && typeof month === "object") {
            const m = month as { year?: number; month?: number };
            return `${m.year}-${String(m.month || 0).padStart(2, "0")}`;
          }
          return "";
        };

        const currentMonthKey = (tempMonth || "").slice(0, 7);

        const favsByLocation = new Map(
          (
            favoriteOriginsByUser.find(
              (o) => o.product.toUpperCase() === product.toUpperCase(),
            )?.favorites || []
          ).map((f) => [f.location.id, f]),
        );

        const originLocations = allOrigins
          .map((origin) => {
            const locationDetail = locationDetailsById.get(origin.location.id);

            if (!locationDetail) {
              console.error(
                `[LocationDetails] Missing batch response for origin "${origin.location.name}" (${origin.location.id})`,
              );
              return null;
            }

            const lat = Number(locationDetail.coordinates?.[0]) || 0;
            const long = Number(locationDetail.coordinates?.[1]) || 0;

            const favOrigin = favsByLocation.get(origin.location.id);
            const matchedMonth =
              favOrigin?.months?.find(
                (m) => toMonthKey(m.month) === currentMonthKey,
              ) || favOrigin?.months?.[0];
            const periodPrices = matchedMonth?.periods?.[0]?.prices;

            const basePrices = {
              truckMaxPrice: origin.truckMaxPrice,
              truckMinPrice: origin.truckMinPrice,
              railMaxPrice: origin.railMaxPrice,
              railMinPrice: origin.railMinPrice,
            };

            const isPriceShowAs = (showAs?: string) =>
              (showAs || "").toUpperCase() === "PRICE";

            const resolvedPrices = (() => {
              if (!originManager || !periodPrices) {
                return basePrices;
              }

              const postedPrice = periodPrices.posted?.price ?? null;
              const truckAltPrice = periodPrices.truck?.price ?? null;
              const railAltPrice = periodPrices.rail?.price ?? null;

              const shouldUseTruckPrice = isPriceShowAs(
                periodPrices.truck?.showAs,
              );
              const shouldUseRailPrice = isPriceShowAs(
                periodPrices.rail?.showAs,
              );

              if (origin.competitor) {
                return {
                  ...basePrices,
                  truckMaxPrice: shouldUseTruckPrice
                    ? truckAltPrice
                    : basePrices.truckMaxPrice,
                  railMaxPrice: shouldUseRailPrice
                    ? railAltPrice
                    : basePrices.railMaxPrice,
                };
              }

              const next = { ...basePrices };

              if (shouldUseTruckPrice) {
                next.truckMaxPrice = postedPrice;
                next.truckMinPrice = truckAltPrice;
              }

              if (shouldUseRailPrice) {
                next.railMaxPrice = postedPrice;
                next.railMinPrice = railAltPrice;
              }

              return next;
            })();

            const { truckMaxPrice, truckMinPrice, railMaxPrice, railMinPrice } =
              resolvedPrices;

            return {
              id: origin.location.id,
              name: origin.location.name,
              owner: locationDetail.owner || "",
              address: locationDetail.address || "",
              city: locationDetail.city || "",
              state: locationDetail.state || "",
              zip: locationDetail.zipCode || "",
              country: locationDetail.country || "",
              price: null,
              lat,
              long,
              products: product ? [product] : [],
              modes: [],
              carriers: [],
              pinType: (origin.competitor ? "competitor" : "koch") as
                | "koch"
                | "competitor"
                | "customer"
                | "kochAndCompetitor"
                | "zipCodes",
              // Include FOB and ALT prices for pin labels
              truckMaxPrice,
              truckMinPrice,
              railMaxPrice,
              railMinPrice,
              postedPrice: periodPrices?.posted?.price ?? null,
              postedShowAs: periodPrices?.posted?.showAs,
              railEnabled: origin.railEnabled,
              truckEnabled: origin.truckEnabled,
              truckShowAs: periodPrices?.truck?.showAs,
              railShowAs: periodPrices?.rail?.showAs,
            } as IPinExtension;
          })
          .filter((loc): loc is NonNullable<typeof loc> => loc !== null);

        const hasKoch = originLocations.some((l) => l.pinType === "koch");
        const hasCompetitor = originLocations.some(
          (l) => l.pinType === "competitor",
        );
        const newPartyTypes = [];
        if (hasKoch) newPartyTypes.push("koch");
        if (hasCompetitor) newPartyTypes.push("competitor");

        if (requestId !== locationDetailsRequestIdRef.current) {
          return;
        }

        useNitroStore.setState({
          locations: originLocations,
          partyTypes: newPartyTypes,
        });
      } catch (error) {
        console.error("Failed to fetch location coordinates", error);
      }
    };

    fetchLocationDetails();
  }, [
    isMobile,
    isOriginManagerMobile,
    legacyIsLoading,
    kochOrigins,
    competitorOrigins,
    showMobileFavoriteOriginPanel,
    product,
    tempParty,
    tempCity,
    tempState,
    tempLocation,
    fetchFavoriteOrigins,
    favoriteOriginsByUser,
    tempMonth,
    originManager,
  ]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSearchableChange = useCallback((isSearchable: boolean) => {}, []);
  const handleProductChange = useCallback(
    (newProduct: string) => {
      setTempProduct(newProduct);
      setProduct(newProduct);
    },
    [setProduct],
  );

  useEffect(() => {
    const hasChangeNew =
      tempProduct !== product ||
      tempMode !== mode ||
      JSON.stringify(tempPartyTypes) !== JSON.stringify(partyTypes);

    if (hasChangeNew) {
      setHasAdditionalFilterChange(true);
    } else {
      setHasAdditionalFilterChange(false);
    }
  }, [
    setHasAdditionalFilterChange,
    tempProduct,
    tempMode,
    tempPartyTypes,
    currentFilters,
    product,
    mode,
    partyTypes,
  ]);

  if (isMobile) {
    return (
      <MobileFilter
        tempParty={tempParty}
        setTempParty={setTempParty}
        tempCity={tempCity}
        setTempCity={setTempCity}
        tempState={tempState}
        setTempState={setTempState}
        tempLocation={tempLocation}
        setTempLocation={setTempLocation}
        tempMonth={tempMonth}
        setTempMonth={setTempMonth}
        tempSearchType={tempSearchType}
        setTempSearchType={setTempSearchType}
        search={mobileSearch}
        hasChange={hasChange}
        product={tempProduct}
        setProduct={handleProductChange}
        mode={tempMode}
        setMode={setTempMode}
        onSearchableChange={handleSearchableChange}
        externalOpen={false}
        setExternalOpen={() => {}}
        panelsOnly
        onFooterSelect={onFooterSelect}
      />
    );
  }

  return (
    <div className="desktop-filter-container">
      {/* Filter popup */}
      {showAdditionalFilters && (
        <AdditionalFiltersModal
          product={tempProduct}
          setProduct={setTempProduct}
          mode={tempMode}
          setMode={setTempMode}
          partyTypes={tempPartyTypes}
          setPartyTypes={setTempPartyTypes}
          search={() => {
            search({
              newProduct: tempProduct,
              newMode: tempMode,
              newPartyTypes: tempPartyTypes,
            });
          }}
          clear={() => {
            setTempProduct("");
            setTempMode("");
            setTempPartyTypes(["koch", "competitor", "customer"]);
            search({ newPartyTypes: ["koch", "competitor", "customer"] });
            setShowAdditionalFilters(false);
          }}
          reset={() => {
            setTempProduct(product);
            setTempMode(mode);
            setTempPartyTypes(partyTypes);
          }}
          close={() => {
            setShowAdditionalFilters(false);
          }}
          hasChange={hasAdditionalFilterChange}
        />
      )}

      <div className="filterBar">
        <FilterObj
          currentField={currentField}
          setCurrentField={setCurrentField}
          tempParty={tempParty}
          setTempParty={setTempParty}
          tempCity={tempCity}
          setTempCity={setTempCity}
          tempState={tempState}
          setTempState={setTempState}
          tempLocation={tempLocation}
          setTempLocation={setTempLocation}
          tempMonth={tempMonth}
          setTempMonth={setTempMonth}
          setTempMode={setTempMode}
          hasChange={hasChange}
          disabled={searchDisabled}
          onClearWhoWhere={() => {
            skipUnsavedPopupForNextSearchRef.current = true;
          }}
        />

        {!isMobile && (
          <HeaderProductFilter
            value={product || defaultProduct}
            disabled={disableProductFilter}
            setValue={(value) => {
              const nextProduct = value || defaultProduct;

              if (nextProduct === product) {
                return;
              }

              if (originManagerHasUnsavedPriceEdits) {
                pendingBlockedSearchRef.current = {
                  filters: {
                    party: currentFilters.party,
                    partyTypes,
                    city: currentFilters.city,
                    state: currentFilters.state,
                    location: currentFilters.location,
                    month: currentFilters.month,
                  },
                  mode,
                  product: nextProduct,
                  partyTypes,
                  hasAdditionalFilterChange: false,
                  labelTypeSnapshot: labelType || "",
                  shouldCloseOriginManager: false,
                };
                triggerOriginManagerUnsavedNavAttempt("search");
                return;
              }

              setProduct(nextProduct);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Filters;
